import { makeLinkComponent } from 'braid-design-system';
import omit from 'object.omit';
import React from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { useAnalytics } from 'src/modules/analytics';

import { getDataTrackingParams } from './dataTrackingParams';

const isInternal = (href: string) => href.match(/\/invoicing/i);

interface LinkProps {
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  ['data-analytics']?: string;
  ['data-trackingParams']?: string;
  href?: string;
  actionOrigin?: string;
}

export const Link = React.forwardRef(
  (componentProps: LinkProps, ref: React.ForwardedRef<HTMLAnchorElement>) => {
    const { trackLink } = useAnalytics();

    const {
      'data-analytics': dataAnalytics,
      actionOrigin,
      href,
      onClick,
    } = componentProps;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      const scope = actionOrigin ? { actionOrigin } : {};

      const trackingParams: Record<string, unknown> = getDataTrackingParams(
        componentProps['data-trackingParams'],
      );

      trackLink(dataAnalytics || href || 'no-link-name', {
        ...scope,
        ...trackingParams,
      });

      onClick?.(e);
    };

    // @ts-expect-error Some props are injected via consuming components and are not
    // present in the LinkProps interface
    const props = omit(componentProps, [
      'authenticationStatuses',
      'staticContext',
      'match',
      'location',
      'history',
      'actionOrigin',
    ]);

    if (!href) {
      // eslint-disable-next-line no-console
      console.warn('LinkRenderer', 'Rendering with no href for', props);
    }

    if (isInternal(href!)) {
      return (
        <ReactRouterLink
          {...props}
          ref={ref}
          to={href!}
          onClick={handleClick}
        />
      );
    }

    return <a {...props} ref={ref} href={href} onClick={handleClick} />;
  },
);

export const LinkComponent = makeLinkComponent((props, ref) => (
  <Link {...(props as LinkProps)} ref={ref} />
));
